// Pure helpers for the Talk BI KPI tile, pivot and flat table (spec §7). No DOM, no class names:
// bi-tables.jsx draws; this file reads the server's rows and does the arithmetic of the look.
import { toNumber, timeAxis } from './bi-charts.mjs';

const DASH = '—';
export const GRAINS = ['year', 'quarter', 'month']; // app/bi/definition.py GRAINS
const id = (v) => JSON.stringify(v);

/**
 * A pivot PanelResult -> the table's parts. columns are [row dims…, col?, 'm0', '__n', '__g0'…]; every
 * row comes from ONE GROUPING SETS query, so its __g flags say what it is: all 1 the grand total,
 * 1 only on the col dim a row total, 0 on the col dim and 1 on the rest a column total, all 0 a cell.
 * Totals are read from those rows, never summed here. A truncated result keeps no column or grand
 * total (its cells no longer add up to them) and drops its last row, the one the cut may have split.
 */
export function pivotize({ columns, rows = [], groups = 0, truncated = false }) {
  const flagAt = columns.flatMap((c, i) => (/^__g\d+$/.test(c) ? [i] : []));
  const m = columns.indexOf('m0');
  const nd = flagAt.length;
  const kind = (r) => flagAt.map((i) => (Number(r[i]) === 1 ? '1' : '0')).join('');
  const hasCol = nd > 1 && rows.some((r) => /^0+1$|^1+0$/.test(kind(r)));
  const nr = hasCol ? nd - 1 : nd;
  const rowKeyOf = (r) => r.slice(0, nr);
  const cells = new Map(), rowTotals = new Map(), colTotals = new Map();
  let grand = null, colOrder = [], cellRows = [];
  for (const r of rows) {
    const k = kind(r), v = toNumber(r[m]);
    if (!k.includes('0')) grand = v;
    else if (!k.includes('1')) cellRows.push(r);
    else if (k.endsWith('1')) rowTotals.set(id(rowKeyOf(r)), v);
    else { colTotals.set(id(r[nr]), v); colOrder.push(r[nr]); }
  }
  if (truncated && hasCol && cellRows.length) {
    const last = id(rowKeyOf(cellRows[cellRows.length - 1]));
    cellRows = cellRows.filter((r) => id(rowKeyOf(r)) !== last);
  }
  const seenRows = new Map(), seenCols = new Set();
  for (const r of cellRows) {
    const rk = rowKeyOf(r);
    seenRows.set(id(rk), rk);
    seenCols.add(id(hasCol ? r[nr] : null));
    cells.set(id([...rk, hasCol ? r[nr] : null]), toNumber(r[m]));
  }
  const rowTotal = (rk) => (hasCol ? rowTotals.get(id(rk)) : cells.get(id([...rk, null]))) ?? null;
  let rowKeys = [...seenRows.values()];
  if (!columns.slice(0, nr).some((d) => GRAINS.includes(d))) {
    // ranked like the comp; a missing total sinks, ties keep the server's order (sort is stable)
    rowKeys = rowKeys.slice().sort((a, b) => (rowTotal(b) ?? -Infinity) - (rowTotal(a) ?? -Infinity));
  }
  return {
    rowDims: columns.slice(0, nr),
    colDim: hasCol ? columns[nr] : null,
    rowKeys,
    colKeys: hasCol ? colOrder.filter((c) => seenCols.has(id(c))) : [],
    cell: (rk, c = null) => cells.get(id([...rk, c])) ?? null,
    rowTotal,
    colTotal: (c) => (truncated ? null : colTotals.get(id(c)) ?? null),
    grand: truncated ? null : grand,
    truncated: truncated ? { shown: cellRows.length, of: groups } : null,
  };
}

// The change of a against the previous b, measured against |b| so a negative base still reads right.
export function changeOf(a, b) {
  const x = toNumber(a), y = toNumber(b);
  if (!x || !y) return DASH;
  const pct = Math.round(((x - y) / Math.abs(y)) * 1000) / 10;
  return { pct, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

const r1 = (v) => Math.round(v * 10) / 10;
// The comp's ramp: the largest cell gets 42% of the heat colour, a zero or negative cell none.
export const heatPct = (v, max) => (v > 0 && max > 0 ? r1(Math.min(1, v / max) * 42) : 0);
export const barPct = (v, max) => (v > 0 && max > 0 ? r1(Math.min(1, v / max) * 100) : 0);

// A sparkline's area and line in a w×h box; a missing value breaks both. Fewer than two points: null.
export function sparkPath(values, w = 120, h = 36) {
  const ys = values.map(toNumber);
  const xs = ys.filter(Number.isFinite);
  if (xs.length < 2) return null;
  const lo = Math.min(...xs), hi = Math.max(...xs);
  const step = w / (ys.length - 1);
  const pt = (i, v) => `${r1(i * step)},${r1(hi === lo ? h / 2 : 2 + (1 - (v - lo) / (hi - lo)) * (h - 4))}`;
  let line = '', area = '', run = [];
  const flush = () => {
    if (run.length > 1) {
      const pts = run.map(([i, v]) => pt(i, v)).join('L');
      line += `M${pts}`;
      area += `M${r1(run[0][0] * step)},${h}L${pts}L${r1(run[run.length - 1][0] * step)},${h}Z`;
    }
    run = [];
  };
  ys.forEach((v, i) => (Number.isFinite(v) ? run.push([i, v]) : flush()));
  flush();
  return line ? { line, area } : null;
}

// Display text for one dim's values: a time grain reads as a date (the server sends date_trunc stamps).
// A NULL group (undated rows) is a dash; the other values still read as dates.
export function dimLabels(values, dim, lang = 'he') {
  const blank = (v) => v == null || v === '';
  const parts = values.map((v) => (blank(v) ? null : /^(\d{4})-(\d{2})/.exec(String(v))));
  if (!GRAINS.includes(dim) || values.some((v, i) => !blank(v) && !parts[i])) {
    return values.map((v) => (blank(v) ? DASH : String(v)));
  }
  if (dim === 'month') {
    const ticks = timeAxis(values.filter((v) => !blank(v)), lang).ticks;
    let j = 0;
    return values.map((v) => (blank(v) ? DASH : ticks[j++]));
  }
  if (dim === 'year') return parts.map((p) => (p ? p[1] : DASH));
  const years = new Set(parts.filter(Boolean).map((p) => p[1])).size > 1;
  return parts.map((p) => {
    if (!p) return DASH;
    const q = Math.ceil(Number(p[2]) / 3);
    const base = lang === 'he' ? `רבעון ${q}` : `Q${q}`;
    return years ? `${base} ${p[1]}` : base;
  });
}

// A pivot's row header text, one per rowKey; two row dims join as the comp's builder does.
export function rowLabels(pivot, lang) {
  const perDim = pivot.rowDims.map((d, i) => dimLabels(pivot.rowKeys.map((k) => k[i]), d, lang));
  return pivot.rowKeys.map((_, j) => perDim.map((labels) => labels[j]).join(' · '));
}

// A sorted copy: measures by value, text by locale; a blank sorts last in either direction.
export function sortRows(rows, key, dir, numeric, lang = 'he') {
  const sign = dir === 'ascending' ? 1 : -1;
  const blank = (v) => (numeric ? toNumber(v) === null : v == null || v === '');
  const cmp = numeric
    ? (a, b) => toNumber(a) - toNumber(b)
    : (a, b) => String(a).localeCompare(String(b), lang === 'he' ? 'he' : 'en', { numeric: true });
  return rows.slice().sort((a, b) => {
    const x = a[key], y = b[key];
    if (blank(x) || blank(y)) return blank(x) - blank(y);
    return sign * cmp(x, y);
  });
}
